import { Directive, Input, OnDestroy, TemplateRef, ViewContainerRef } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import * as selectors from '../../store/reducers/index';
import { OrganisationConfigDto } from '@careapp-group/ng-sdk';

/**
 * Will only display the element if the current organisation config value
 * matches the value passed in. This will either retrieve values from the server
 * or default to false.
 *
 * Usage:
 *
 * 1. Within your .ts file, import the OrganisationConfigDirectiveCheck:
 * > import { OrganisationConfigDirectiveCheck } from '.../organisation-config.directive';
 *
 * 2. Create an object describing the key/values expected:
 * > testOrgConfigItem: OrganisationConfigDirectiveCheck =
 * >   { key: 'videoChatEnabled', expected: true };
 *
 * 3. In your html, the directive can now be used:
 * >  <div *orgConfig="testConfigItem">
 * >    Hello world!
 * >  </div>
 */

export interface OrganisationConfigDirectiveCheck {
  key: string;
  expected: any;
}

@Directive({
    selector: '[orgConfig]',
    standalone: false
})
export class OrganisationConfigDirective implements OnDestroy {
  orgConfig$: Observable<any>;
  orgConfigSubscription: Subscription;
  configData: OrganisationConfigDto;
  isSatisfied: boolean;
  _rendered: boolean;

  constructor(
    public store: Store<any>,
    private templateRef: TemplateRef<any>,
    private viewContainerRef: ViewContainerRef
  ) {
    this.orgConfig$ = this.store.select(selectors.org.currentOrgConfiguration);
    this.orgConfigSubscription = this.orgConfig$.subscribe((config) => {
      this.configData = <any>{
        ...(config || {}),
      };
      this._updateView();
    });
  }

  private _condition: OrganisationConfigDirectiveCheck;
  get condition() {
    return this._condition;
  }

  @Input() set orgConfig(condition: OrganisationConfigDirectiveCheck) {
    this._condition = condition;
    this._updateView();
  }

  private _updateView() {
    const config = this.configData || {};
    const { expected, key } = this.condition || { key: '', expected: true };
    if (config[key] === expected && !this._rendered) {
      this.viewContainerRef.createEmbeddedView(this.templateRef);
      this._rendered = true;
    } else if (config[key] !== expected && this._rendered) {
      this.viewContainerRef.clear();
      this._rendered = false;
    }
  }

  ngOnDestroy() {
    this.orgConfigSubscription.unsubscribe();
  }
}
